import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_josmart-staging/artifacts/r0wcvy09_file_00000000fee871f598adf429b9fe9c58.png";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { t, language } = useLanguage();

  const navLinks = [
    { href: '#hero', label: language === 'fr' ? 'Accueil' : 'Home' },
    { href: '#services', label: t('nav_services') },
    { href: '#products', label: t('nav_products') },
    { href: '#process', label: language === 'fr' ? 'Processus' : 'Process' },
    { href: '#portfolio', label: 'Portfolio' },
    { href: '#testimonials', label: t('nav_testimonials') },
    { href: '#faq', label: t('nav_faq') },
    { href: '#contact', label: t('nav_contact') },
  ];

  // Quick links visible on mobile between logo and hamburger
  const mobileQuickLinks = [
    { href: '#services', label: 'Services' },
    { href: '#products', label: language === 'fr' ? 'Produits' : 'Products' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      window.location.href = '/' + href;
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      const navbarHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - navbarHeight,
        behavior: 'smooth'
      });
    }
    setIsOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 navbar-glass" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="navbar-logo">
            <img 
              src={LOGO_URL} 
              alt="JoSmarTech" 
              className="h-12 w-auto object-contain brightness-125 contrast-110 rounded-lg"
              style={{ background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', mixBlendMode: 'screen' }}
            />
          </Link>

          {/* Mobile Quick Links - visible between logo and hamburger */}
          <div className="flex md:hidden items-center gap-3 flex-1 justify-center">
            {mobileQuickLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-white/70 hover:text-white text-xs font-medium transition-colors"
                data-testid={`mobile-quick-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <Link to="/blog" className="text-white/70 hover:text-white text-xs font-medium transition-colors">
              Blog
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-white/80 hover:text-white font-medium transition-colors duration-200"
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </a>
            ))}
            <Link
              to="/payment"
              className="text-white/80 hover:text-white font-medium transition-colors duration-200"
              data-testid="nav-link-payment"
            >
              {language === 'fr' ? 'Paiement' : 'Payment'}
            </Link>
            <Link
              to="/blog"
              className="text-white/80 hover:text-white font-medium transition-colors duration-200"
              data-testid="nav-link-blog"
            >
              Blog
            </Link>
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <LanguageSwitcher />
            <a
              href="#contact"
              onClick={(e) => scrollToSection(e, '#contact')}
              className="px-6 py-2.5 bg-[#4A90D9] text-white rounded-full font-semibold hover:bg-[#5BA0E9] transition-colors duration-200"
              data-testid="navbar-cta"
            >
              {t('nav_cta')}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-white flex-shrink-0"
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden mobile-menu"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => scrollToSection(e, link.href)}
                  className="block text-white/80 hover:text-white font-medium py-2"
                >
                  {link.label}
                </a>
              ))}
              <Link
                to="/payment"
                onClick={() => setIsOpen(false)}
                className="block text-white/80 hover:text-white font-medium py-2"
              >
                {language === 'fr' ? 'Paiement' : 'Payment'}
              </Link>
              <Link
                to="/blog"
                onClick={() => setIsOpen(false)}
                className="block text-white/80 hover:text-white font-medium py-2"
              >
                Blog
              </Link>
              <Link
                to="/jobs"
                onClick={() => setIsOpen(false)}
                className="block text-white/80 hover:text-white font-medium py-2"
              >
                {language === 'fr' ? 'Emplois' : 'Jobs'}
              </Link>
              <Link
                to="/rendez-vous"
                onClick={() => setIsOpen(false)}
                className="block text-white/80 hover:text-white font-medium py-2"
              >
                {language === 'fr' ? 'Rendez-vous' : 'Appointment'}
              </Link>
              <div className="py-2">
                <LanguageSwitcher />
              </div>
              <a
                href="#contact"
                onClick={(e) => scrollToSection(e, '#contact')}
                className="block w-full text-center px-6 py-3 bg-[#4A90D9] text-white rounded-full font-semibold mt-4"
              >
                {t('nav_cta')}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
